import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  VStack,
  Input,
  IconButton,
  HStack,
  Text,
  useColorModeValue,
  Spinner,
  Button,
  Tooltip,
  Heading,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  useBreakpointValue,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
  useToast,
} from '@chakra-ui/react';
import { FiSend, FiMenu, FiPlus } from 'react-icons/fi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAI } from '../../contexts/AIContext';
import { ChatHistory } from '../../contexts/AIContext';
import { format } from 'date-fns';
import ConversationList from './components/ConversationList';
import AIPreferences from './components/AIPreferences';
import { useSubscription } from '../../contexts/SubscriptionContext';

const ChatPage: React.FC = () => {
  const [message, setMessage] = useState('');
  const { chatHistory, sendMessage, clearChat, loadConversation, createConversation, currentConversation, setChatHistory } = useAI();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { subscription, updateMessageCount } = useSubscription();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const userBubbleBg = useColorModeValue('blue.500', 'blue.200');
  const userTextColor = useColorModeValue('white', 'gray.800');
  const aiBubbleBg = useColorModeValue('gray.100', 'gray.700');
  const isMobile = useBreakpointValue({ base: true, md: false });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory.messages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() || chatHistory.isLoading) return;

    const trimmedMessage = message.trim();
    setMessage(''); // Clear message immediately on submit
    
    try {
      let conversationId = currentConversation?.id;
      
      // If no conversation exists, create one first
      if (!conversationId) {
        const title = trimmedMessage.slice(0, 50) + (trimmedMessage.length > 50 ? '...' : '');
        conversationId = await createConversation(title);
      }

      // Add user message to chat history immediately
      setChatHistory((prev: ChatHistory) => ({
        messages: [
          ...prev.messages,
          { role: 'user', content: trimmedMessage, timestamp: new Date().toISOString() }
        ],
        isLoading: true
      }));
      
      const response = await sendMessage(trimmedMessage, conversationId);
      // Update message count from response
      if (response.messageCount) {
        updateMessageCount(response.messageCount);
      }
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      toast({
        title: 'Error',
        description: 'Failed to send message',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setMessage(trimmedMessage); // Restore the message if it failed
      // Reset chat history loading state
      setChatHistory((prev: ChatHistory) => ({
        ...prev,
        isLoading: false
      }));
    }
  };

  const handleNewChat = () => {
    // Just clear the current conversation and chat history
    clearChat();
  };

  const handleConversationSelect = (conversationId: string) => {
    loadConversation(conversationId);
    if (isMobile) {
      onClose();
    }
  };

  const suggestedPrompts = [
    {
      title: "Analyze My Mood Patterns",
      prompt: "Can you analyze my mood patterns from my recent journal entries and provide insights?"
    },
    {
      title: "Weekly Reflection",
      prompt: "Help me reflect on my week based on my journal entries. What patterns or insights do you notice?"
    },
    {
      title: "Goal Progress",
      prompt: "Based on my journal entries, how am I progressing towards my goals and what could I improve?"
    },
    {
      title: "Emotional Growth",
      prompt: "Can you analyze my emotional growth over time using my journal entries?"
    }
  ];

  const handleSuggestedPrompt = async (prompt: string) => {
    try {
      setMessage(''); // Clear message immediately
      let conversationId = currentConversation?.id;
      
      // If no conversation exists, create one first
      if (!conversationId) {
        const title = prompt.slice(0, 50) + (prompt.length > 50 ? '...' : '');
        conversationId = await createConversation(title);
      }

      // Add user message to chat history immediately
      setChatHistory((prev: ChatHistory) => ({
        messages: [
          ...prev.messages,
          { role: 'user', content: prompt, timestamp: new Date().toISOString() }
        ],
        isLoading: true
      }));
      
      const response = await sendMessage(prompt, conversationId);
      // Update message count from response
      if (response.messageCount) {
        updateMessageCount(response.messageCount);
      }
    } catch (error) {
      console.error('Error in handleSuggestedPrompt:', error);
      toast({
        title: 'Error',
        description: 'Failed to send message',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      // Reset chat history loading state
      setChatHistory((prev: ChatHistory) => ({
        ...prev,
        isLoading: false
      }));
    }
  };

  const sidebarContent = (
    <Tabs height="100%" display="flex" flexDirection="column">
      <TabList>
        <Tab>Conversations</Tab>
        <Tab>Settings</Tab>
      </TabList>
      <TabPanels flex={1} overflow="hidden">
        <TabPanel p={0} height="100%">
          <ConversationList onSelect={handleConversationSelect} />
        </TabPanel>
        <TabPanel p={0} height="100%" overflowY="auto">
          <AIPreferences />
        </TabPanel>
      </TabPanels>
    </Tabs>
  );

      return (
    <Box p={6} height="100vh" display="flex">
      {/* Mobile Menu Button */}
      {isMobile && (
        <IconButton
          aria-label="Open menu"
          icon={<FiMenu />}
          position="fixed"
          top={4}
          left={4}
          zIndex={2}
          onClick={onOpen}
        />
      )}

      {/* Sidebar */}
      {isMobile ? (
        <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="md">
          <DrawerOverlay>
            <DrawerContent>
              <DrawerCloseButton />
              <DrawerHeader>Chat</DrawerHeader>
              <DrawerBody p={0} display="flex" flexDirection="column">
                {sidebarContent}
              </DrawerBody>
            </DrawerContent>
          </DrawerOverlay>
        </Drawer>
      ) : (
        <Box
          width="350px"
          height="100%"
          borderRight="1px solid"
          borderColor={borderColor}
          bg={bgColor}
          mr={6}
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          {sidebarContent}
        </Box>
            )}

      {/* Main Chat Area */}
      <Box flex={1} display="flex" flexDirection="column" overflow="hidden">
        <HStack justify="space-between" mb={6}>
          <VStack align="start" spacing={1}>
            <Heading size="lg">AI Chat Assistant</Heading>
            {!subscription?.isSubscribed && (
              <Text fontSize="sm" color="gray.500">
                {subscription?.monthlyMessageCount === 15 ? (
                  `Message limit reached. Upgrade to Premium for unlimited messages. Your message count will reset on ${subscription?.messageCountResetDate ? new Date(subscription?.messageCountResetDate).toLocaleDateString() : 'unknown'}`
                ) : (
                  `${15 - (subscription?.monthlyMessageCount || 0)} messages remaining this month`
                )}
              </Text>
            )}
          </VStack>
          <Tooltip label="Start a new chat">
            <IconButton
              aria-label="New chat"
              icon={<FiPlus />}
              onClick={handleNewChat}
              variant="ghost"
            />
          </Tooltip>
        </HStack>

        {/* Chat Messages Area */}
        <Box
          flex={1}
          overflowY="auto"
          px={4}
          mb={6}
          borderRadius="lg"
          bg={useColorModeValue('gray.50', 'gray.700')}
        >
          {chatHistory.messages.length === 0 && !currentConversation ? (
            <VStack spacing={6} py={8}>
              <Text fontSize="lg" fontWeight="medium">
                Welcome to your AI Journal Assistant
              </Text>
              <Text textAlign="center" color="gray.500">
                I can help you analyze your journal entries, track patterns, and provide insights.
                Try one of these suggestions or ask your own question:
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} width="100%" maxW="800px">
                {suggestedPrompts.map((item, index) => (
                <Button
                    key={index}
                    height="auto"
                    p={4}
                    whiteSpace="normal"
                    textAlign="left"
                    variant="outline"
                    onClick={() => handleSuggestedPrompt(item.prompt)}
        >
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="bold">{item.title}</Text>
                      <Text fontSize="sm" color="gray.500">
                        {item.prompt}
                      </Text>
                    </VStack>
                </Button>
                ))}
              </SimpleGrid>
            </VStack>
          ) : (
            <VStack spacing={4} align="stretch" py={4}>
              {chatHistory.messages.map((msg, index) => (
                <Box
                  key={index}
                  alignSelf={msg.role === 'user' ? 'flex-end' : 'flex-start'}
                  maxW="80%"
                >
                  <Box
                  bg={msg.role === 'user' ? userBubbleBg : aiBubbleBg}
                  color={msg.role === 'user' ? userTextColor : 'inherit'}
                    p={4}
          borderRadius="lg"
                >
                    {msg.role === 'assistant' ? (
                      <Box className="markdown-body">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {msg.content}
                  </ReactMarkdown>
                      </Box>
                    ) : (
                      <Text>{msg.content}</Text>
                    )}
                  </Box>
                  <Text
                    fontSize="xs"
                    color="gray.500"
                    mt={1}
                    textAlign={msg.role === 'user' ? 'right' : 'left'}
        >
                    {format(new Date(msg.timestamp), 'h:mm a')}
                  </Text>
                </Box>
              ))}
              {chatHistory.isLoading && (
                <Box alignSelf="flex-start">
                  <Spinner size="sm" mr={2} />
                  <Text display="inline" fontSize="sm" color="gray.500">
                    Thinking...
                  </Text>
                </Box>
              )}
              <div ref={messagesEndRef} />
            </VStack>
          )}
            </Box>

        {/* Input Form */}
        <Box
          as="form"
          onSubmit={handleSubmit}
          bg={bgColor}
          p={4}
          borderRadius="lg"
          border="1px solid"
          borderColor={borderColor}
        >
              <HStack>
                <Input
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Ask me anything about your journal entries..."
                  disabled={chatHistory.isLoading || (!subscription?.isSubscribed && subscription?.monthlyMessageCount === 15)}
                />
                <Tooltip
                  label={(!subscription?.isSubscribed && subscription?.monthlyMessageCount === 15) ? 
                    "Upgrade to Premium for unlimited messages" : undefined}
                >
                  <IconButton
                    aria-label="Send message"
                    icon={<FiSend />}
                    type="submit"
                    colorScheme="blue"
                    disabled={!message.trim() || chatHistory.isLoading || (!subscription?.isSubscribed && subscription?.monthlyMessageCount === 15)}
                  />
                </Tooltip>
              </HStack>
        </Box>
        </Box>
    </Box>
  );
};

export default ChatPage; 